import { declinesSessionWindow } from "../constants";
import { useEffect, useMemo, useRef } from "react";
import type { ConfiguratorSharedProps } from "../../widgetDefinition";
import { useSelector } from "react-redux";
import { Collapse, Empty, Select, Space, Tooltip, Typography } from "antd";
import { InfoCircleOutlined } from "@ant-design/icons";
import CompactStatisticNumber from "@components/StatTileV2/CompactStatisticNumberSuffix";
import type { MetricOption, SubjectEntity, StatComponentSpec, StatTileNonSeriesResponse } from "../../Processing/types";
import { ConfigPanelLayout } from "../shared/ConfigPanelLayout";
import { SlicerTokenInput } from "../shared/SlicerTokenInput";
import { PreviewDisclaimer } from "../shared/PreviewDisclaimer";
import { DateRangeForm } from "../shared/DateRangeForm";
import { PlayersForm } from "../shared/PlayersForm";
import { MetricPicker } from "../shared/MetricPicker";
import {
  CountIncludePicker,
  countSourceFromMetricId,
  defaultCountSpec,
} from "../shared/CountIncludePicker";
import { useConfiguratorSync } from "../shared/useConfiguratorSync";
import { ToggleOption } from "../shared/ToggleOption";
import { isSectionBindable } from "../../Processing/controllerBindings";
import { SectionSummary } from "../shared/SectionSummary";
import {
  statReducer,
  createInitialState,
  hydrateFromSpec,
  formStateToSpec,
} from "./statState";
import { describeStatWindow } from "./comparisonHints";

const { Panel } = Collapse;
const { Text } = Typography;



export function StatConfigurator({
  onSpecChange,
  existingSpec,
  metrics,
  getSubjectEntities,
}: ConfiguratorSharedProps<StatComponentSpec>) {
  const comparisonTypes: { label: string; value: string }[] =
    useSelector(({ team }: any) => team?.misc?.comparison_types) ?? [];

  const [state, dispatch] = useConfiguratorSync({
    reducer: statReducer,
    existingSpec,
    hydrate: hydrateFromSpec,
    createInitial: createInitialState,
    toSpec: formStateToSpec,
    onSpecChange,
    metrics,
    getSubjectEntities,
  });


  const countSource = countSourceFromMetricId(state.selectedMetricId);
  const metric = metrics.find((m) => m.id === state.selectedMetricId);
  const displayLabel = state.label || metric?.name || "Metric";
  const windowHint = describeStatWindow(state.comparisonType, state.metricAgg, state);
  const dateLabel = windowHint.dateSummary;
  const totalSelected =
    state.selectedSubjects.filter((s) => s !== "full-team").length + state.selectedDrills.length;
  const hasSpecificSubject =
    state.selectedSubjects.some((s) => s !== "full-team") || state.selectedDrills.length > 0;

  const isMounted = useRef(false);
  useEffect(() => {
    if (!isMounted.current) { isMounted.current = true; return; }
    dispatch({ type: "SET_SHOW_TEAM_POS_DATA", value: hasSpecificSubject });
  }, [hasSpecificSubject]);

  // Auto-select first metric when creating a new widget (not editing)
  useEffect(() => {
    if (!existingSpec && state.selectedMetricId === null && metrics.length > 0) {
      const firstMetric = metrics[0];
      dispatch({
        type: "SET_METRIC",
        metricId: firstMetric.id,
        metricName: firstMetric.name,
      });
    }
  }, [existingSpec, metrics]);

  const form = (
    <>
    <div style={{ padding: "12px 16px", borderBottom: "1px solid #e8ecf0" }}>
      <Space size={8} align="center" style={{ width: "100%" }}>
        <Text style={{ fontSize: 12, minWidth: 40 }}>Title</Text>
        <SlicerTokenInput
          size="small"
          placeholder={metric?.name ?? "e.g. Distance"}
          value={state.label}
          onChange={(label) => dispatch({ type: "SET_LABEL", label })}
          style={{ flex: 1 }}
        />
      </Space>
    </div>
    <Collapse
      accordion
      activeKey={state.expandedKey}
      onChange={(key) => dispatch({ type: "SET_EXPANDED_KEY", key })}
      style={{ borderRadius: 0, borderTop: "none" }}
    >
      <Panel
        key="__metric"
        header={
          <div style={{ display: "flex", alignItems: "center", gap: 8, overflow: "hidden", flexWrap: "nowrap" }}>
            <span className="awm-step-number" style={{ flexShrink: 0 }}>1</span>
            <Text strong style={{ fontSize: 13, whiteSpace: "nowrap", flexShrink: 0 }}>Select Metric</Text>
            {metric && (
              <Tooltip title={metric.name} mouseEnterDelay={0.5}>
                <Text type="secondary" style={{ fontSize: 11, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", minWidth: 0, flex: 1 }}>
                  {metric.name}
                </Text>
              </Tooltip>
            )}
          </div>
        }
      >
        <div className="awm-step-body">
          <MetricPicker
            variant="dropdown"
            metrics={metrics}
            selectedMetricId={state.selectedMetricId}
            metricAgg={state.metricAgg}
            activeCategory=""
            searchQuery=""
            onSelectMetric={(id) => {
              const source = countSourceFromMetricId(id);
              // One source owns the include-list, so a metric change replaces it.
              dispatch({ type: "SET_COUNT_SPEC", countSpec: source ? defaultCountSpec(source) : undefined });
              dispatch({
                type: "SET_METRIC",
                metricId: id,
                metricName: metrics.find((m) => m.id === id)?.name,
                prevMetricName: metric?.name,
              });
              // A count has no average or best; the backend accepts only the total.
              if (source) dispatch({ type: "SET_METRIC_AGG", agg: "sum" });
              else if (state.countSpec) dispatch({ type: "SET_METRIC_AGG", agg: "avg" });
            }}
            onAggChange={(agg) => dispatch({ type: "SET_METRIC_AGG", agg })}
            onCategoryChange={() => {}}
            onSearchChange={() => {}}
            countSlot={state.countSpec && (
              <div className="awm-labeled-row">
                <Text className="awm-labeled-row-label">Counting</Text>
                <CountIncludePicker
                  spec={state.countSpec}
                  onChange={(countSpec) => dispatch({ type: "SET_COUNT_SPEC", countSpec })}
                />
              </div>
            )}
          />
          {comparisonTypes.length > 0 && !countSource && (
            <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
              <Text type="secondary" style={{ fontSize: 11 }}>Comparison</Text>
              <Select
                size="small"
                value={state.comparisonType}
                options={comparisonTypes}
                onChange={(v) => dispatch({ type: "SET_COMPARISON_TYPE", value: v })}
                style={{ width: "100%", height: 28 }}
              />
              <Text type="secondary" style={{ fontSize: 11 }}>{windowHint.value}</Text>
            </div>
          )}
          {countSource && (
            <Text type="secondary" style={{ fontSize: 11 }}>
              A count compares the selected range against the one before it. Counts have
              no sessions, so the other comparisons do not apply.
            </Text>
          )}
        </div>
      </Panel>

      <Panel
        key="__date_range"
        header={
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <span className="awm-step-number">2</span>
            <Text strong style={{ fontSize: 13 }}>Date Range</Text>
            <SectionSummary kind="date" controllerId={state.dateControllerId} own={dateLabel} />
          </div>
        }
      >
        <DateRangeForm
          keepsOwnDatesOnLatestN={declinesSessionWindow("stat_tile")}
          bindable={isSectionBindable("stat_tile", "date")}
          description={windowHint.dateRange}
          value={state}
          onChange={(patch) => dispatch({ type: "UPDATE_DATE_RANGE", patch })}
        />
      </Panel>

      <Panel
        key="__players"
        header={
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <span className="awm-step-number">3</span>
            <Text strong style={{ fontSize: 13 }}>Filters</Text>
            <SectionSummary kind="subject" controllerId={state.subjectControllerId}
              own={totalSelected > 0 ? `${totalSelected} selected` : "Full Team"}
              getSubjectEntities={getSubjectEntities} />
          </div>
        }
      >
        <div className="awm-step-body">
          <PlayersForm
            bindable={isSectionBindable("stat_tile", "subject")}
            value={state}
            onChange={(patch) => dispatch({ type: "UPDATE_PLAYERS", patch })}
            getSubjectEntities={getSubjectEntities}
            hideDrills
            singleSelect
            description="Choose which athlete, position, or group to include."
          />
          <ToggleOption
            label="Exclude Injured Players"
            description="Exclude injured athletes from this value."
            checked={state.excludeInjuredPlayers}
            onChange={(v) => dispatch({ type: "SET_EXCLUDE_INJURED", value: v })}
          />
        </div>
      </Panel>

      <Panel
        key="__options"
        header={
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <span className="awm-step-number">4</span>
            <Text strong style={{ fontSize: 13 }}>Additional Options</Text>
          </div>
        }
      >
        <div className="awm-step-body">
          <ToggleOption
            label="Show Team and Position Data"
            description="Display team and positional averages in the bottom-right corner of this tile."
            checked={state.showTeamPosData}
            onChange={(v) => dispatch({ type: "SET_SHOW_TEAM_POS_DATA", value: v })}
          />
        </div>
      </Panel>
    </Collapse>
    </>
  );

  const isAsym = metric?.isAsym ?? false;

  const fakeMetricStat = useMemo((): StatTileNonSeriesResponse => ({
    latest_value: isAsym ? 0 : 15.87,
    compared_to_value: isAsym ? null : 14.14,
    latest_date: "Jun 9, 2026",
    diff: isAsym ? null : 1.73,
    low_good: false,
    is_asym: isAsym,
    left: isAsym ? { latest_value: 895.08, diff: 142.08 } : null,
    right: isAsym ? { latest_value: 849.92, diff: 57.70 } : null,
    team: state.showTeamPosData ? { value: isAsym ? "929.61 | 883.61" : "16.34", color: "inherit" } : null,
    pos:  state.showTeamPosData ? { value: isAsym ? "902.55 | 839.2"  : "16.04", color: "inherit" } : null,
    name: displayLabel,
    type: "stat_tile",
  }), [isAsym, state.showTeamPosData, displayLabel]);

  const preview = (
    <div className="stat-preview-shell">
      {state.selectedMetricId != null && (
        <PreviewDisclaimer
          style={{ position: "absolute", top: 10, left: 0, right: 0, padding: "0 16px" }}
        />
      )}
      {state.selectedMetricId == null ? (
        <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Select a metric to see a preview" />
      ) : (
        <div className="stat-preview-tile">
          <div className="widget-header">
            <span style={{ display: "flex", alignItems: "center", gap: 6, minWidth: 0 }}>
              <span style={{ fontSize: 18, fontWeight: 600, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {displayLabel}
              </span>
              <InfoCircleOutlined style={{ fontSize: 13, color: "rgba(0,0,0,0.35)", flexShrink: 0 }} />
            </span>
          </div>
          <div className="stat-tile-content">
            <div className="stat-tile-number-row">
              <CompactStatisticNumber
                metricStat={fakeMetricStat}
                isAsym={isAsym}
                show_delta={true}
                dynamicFontSizing={true}
              />
            </div>
            {state.showTeamPosData && (
              <div className="stat-tile-benchmarks">
                <div style={{ marginBottom: 2, lineHeight: isAsym ? 0.9 : 1.2, fontSize: isAsym ? 11 : 15 }}>
                  T: {fakeMetricStat.team?.value ?? "—"}
                </div>
                <div style={{ marginBottom: 2, lineHeight: isAsym ? 0.9 : 1.2, fontSize: isAsym ? 11 : 15 }}>
                  P: {fakeMetricStat.pos?.value ?? "—"}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );

  return <ConfigPanelLayout form={form} preview={preview} />;
}
